// BARA FAZELOR, aceeași pe toată platforma (A7): o linie pe fază, cu semnul
// stării, numele fazei și nota de lângă — pasul la care e, ce așteaptă, de ce
// nu trece. Planul de situație are Amplasare → Circulație și amenajări (agentul),
// arhitectura Etaje →
// Subsol (sau Import), structura Schița, ori Geometrie macro → Calcul de
// detaliu. Progresul se vede DIRECT, nu doar în tooltip (A9).
//
// Semnul stării e desen (`aria-hidden`), deci starea se scrie și în cuvinte,
// pentru cititorul de ecran: altfel «gata» și «nepornită» nu se auzeau deloc.

import type React from "react";
import { useT } from "../i18n";
import { numeStare, type FazaAfisata } from "../nav/selectie";
import type { Stare } from "../nav/api";
import "../nav/nav.css";

/** Semnul unei stări. Forma e PLIUL semnului Optimus Build — paralelogramul
 *  înclinat ca benzile din antet (stilul din 26.09.2026), nu un cerc: plin
 *  verde cu bifă (gata), plin roșu cu «!» (nu trece), contur cyan care
 *  pulsează (în lucru), contur cu punct (așteaptă / la rând), contur cu
 *  pătrat (oprită), contur gol (nepornită). Bifa și «!» rămân: verdele și
 *  roșul au aproape aceeași luminozitate, deci culoarea singură nu ajunge. */
const PLIU = "M1 12L4.6 2H13L9.4 12Z";
const PLIU_CONTUR = "M1.9 11.3L5.1 2.7H12.1L8.9 11.3Z";

export function SemnStare({ stare, s = 14 }:
                          { stare: Stare | "nepornita" | "sarit" | "exclus"; s?: number }) {
  const cutie = { width: s, height: s, viewBox: "0 0 14 14", "aria-hidden": true as const,
                  className: `semn-stare semn-${stare}` };
  const contur = (c: string, extra?: React.ReactNode) => (
    <svg {...cutie}>
      <path d={PLIU_CONTUR} fill="none" stroke={c} strokeWidth="1.4" strokeLinejoin="round" />
      {extra}
    </svg>
  );
  switch (stare) {
    case "gata":
      return (
        <svg {...cutie}>
          <path d={PLIU} fill="var(--ok)" />
          <path d="M4.9 7.4l1.6 1.5 3.3-3.6" fill="none" stroke="#fff" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "nu_trece":
    case "picata":
      return (
        <svg {...cutie}>
          <path d={PLIU} fill="var(--bad)" />
          <path d="M7.6 4.3l-.5 3.4M6.7 9.9v.1" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case "in_lucru":
      return (
        <svg {...cutie}>
          <path className="semn-arc" d={PLIU} fill="var(--glow)" />
          <path d={PLIU_CONTUR} fill="none" stroke="var(--acc)" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      );
    case "asteapta":
    case "la_rand": {
      const c = stare === "asteapta" ? "var(--amb)" : "var(--mut)";
      return contur(c, <circle cx="7" cy="7" r="1.3" fill={c} />);
    }
    case "oprita":
      return contur("var(--mut)", <rect x="5.4" y="5.4" width="3.2" height="3.2" rx="0.4" fill="var(--mut)" />);
    default:
      return contur("var(--line2)");
  }
}

export default function FazeBara({ faze, orientare = "vertical" }:
                                 { faze: FazaAfisata[]; orientare?: "vertical" | "orizontal" }) {
  const t = useT();
  return (
    <span className={`faze-bara ${orientare}`}>
      {faze.map((f) => (
        <span key={f.cheie} className={`faza faza-${f.stare}`}
              title={f.rafinare ? t("faza.rafinare_titlu") : f.titlu ?? undefined}>
          <SemnStare stare={f.stare} />
          <span>
            <b>{f.nume}</b>
            <span className="doar-cititor"> ({numeStare(f.stare)})</span>
            {f.nota && <span className="faza-nota"> {f.nota}</span>}
          </span>
        </span>
      ))}
    </span>
  );
}
